import type {GetTileDataOptions, TiledImageBase} from "#utils/tiled-image/tiled-image-base"
import {getNumCeilMipLevels} from "#utils/mip-map"
import type {Size2Like, Vector2Like} from "@cm/math"
import {Size2} from "@cm/math"
import type {RetiledImageTile} from "#utils/tiled-image/retiled-image-tile"

export type TileFetchParams = {
    url: string
    remoteTileSize: Size2Like
    remoteTileMargin: number
    tileSize: Size2Like
    tileMargin: number
    remoteMipLevelCorrectionScale: number
    tileIndex: Vector2Like
}

export abstract class RetiledImageBase<TileImageType> {
    protected abstract extractSubTileFromRemoteTile(
        tileData: TileImageType,
        tileIndex: Vector2Like,
        remoteMipLevelCorrectionScale: number,
    ): Promise<RetiledImageTile<TileImageType>>

    constructor(
        readonly tiledImage: TiledImageBase<TileImageType>,
        readonly tileSize: Size2Like,
        readonly tileMargin: number,
    ) {
        this._tileSizeWithMargin = {
            width: this.tileSize.width + 2 * this.tileMargin,
            height: this.tileSize.height + 2 * this.tileMargin,
        }

        // make sure the remote tile size is an integer multiple of the atlas tile size
        const remoteTileSize = {width: this.tiledImage.imageTilesDescriptor.tileWidth, height: this.tiledImage.imageTilesDescriptor.tileHeight}
        this._atlasTilesPerRemoteTile = {
            x: remoteTileSize.width / this.tileSize.width,
            y: remoteTileSize.height / this.tileSize.height,
        }
        if (!Number.isInteger(this._atlasTilesPerRemoteTile.x) || !Number.isInteger(this._atlasTilesPerRemoteTile.y)) {
            throw new Error(
                `RetiledImage: non-integer scale factor between retiled size (${this.tileSize.width}x${this.tileSize.height}) and remote tile size (${remoteTileSize.width}, ${remoteTileSize.height}) currently not supported`,
            )
        }

        // make sure the remote tiles have sufficient margin to supply the atlas tile margin
        const remoteTileMargin = this.tiledImage.imageTilesDescriptor.tileMargin
        if (remoteTileMargin < this.tileMargin) {
            throw new Error("RetiledImage: remote tile margin is smaller than retiled margin")
        }

        this._numTiles = {
            x: Math.ceil(this.tiledImage.imageTilesDescriptor.imageWidth / this.tileSize.width),
            y: Math.ceil(this.tiledImage.imageTilesDescriptor.imageHeight / this.tileSize.height),
        }
        this._numTileMipLevels = getNumCeilMipLevels(Size2.fromVector2Like(this._numTiles))
        this._numImageMipLevels = getNumCeilMipLevels(
            new Size2(this.tiledImage.imageTilesDescriptor.imageWidth, this.tiledImage.imageTilesDescriptor.imageHeight),
        )
    }

    get numTileMipLevels(): number {
        return this._numTileMipLevels
    }

    get numImageMipLevels(): number {
        return this._numImageMipLevels
    }

    get numTiles(): Vector2Like {
        return this._numTiles
    }

    get tileSizeWithMargin(): Size2Like {
        return this._tileSizeWithMargin
    }

    async createTileImage(mipLevel: number, tileIndex: Vector2Like, options?: GetTileDataOptions): Promise<RetiledImageTile<TileImageType>> {
        if (mipLevel < 0 || mipLevel >= this._numImageMipLevels) {
            throw new Error(`RetiledImage: requested mip level ${mipLevel} is out of bounds (0 - ${this._numImageMipLevels - 1})`)
        }
        const remoteMipLevel = Math.min(mipLevel, this.tiledImage.tileIndex.maxMipLevel)
        const remoteMipLevelCorrectionScale = 1 << (mipLevel - remoteMipLevel)
        const remoteTileIndex = {
            x: Math.floor(tileIndex.x / (this._atlasTilesPerRemoteTile.x * remoteMipLevelCorrectionScale)),
            y: Math.floor(tileIndex.y / (this._atlasTilesPerRemoteTile.y * remoteMipLevelCorrectionScale)),
        }
        const imageTileData = await this.tiledImage.getTileData(
            {
                mipLevel: remoteMipLevel,
                tileIndex: remoteTileIndex,
            },
            options,
        )
        return await this.extractSubTileFromRemoteTile(imageTileData, tileIndex, remoteMipLevelCorrectionScale)
    }

    protected get atlasTilesPerRemoteTile(): Vector2Like {
        return this._atlasTilesPerRemoteTile
    }

    private readonly _atlasTilesPerRemoteTile: Vector2Like
    private readonly _numTileMipLevels: number
    private readonly _numImageMipLevels: number
    private readonly _numTiles: Vector2Like
    private readonly _tileSizeWithMargin: Size2Like
}
